import { GetLeadsApiResponse, GetLeadsParams, GetLeadsResponse, LeadDispatchDto, LeadTableRow } from '@/types/Leads';

import { api } from './api';

export type LeadStatus = 'ACCEPTED' | 'REJECTED';
export type LeadRejectionReason = 'CAPACITY' | 'EQUIPMENT' | 'PRICING' | 'DISTANCE' | 'NOT_INTERESTED' | 'OTHER';

type UpdateLeadStatusArgs =
  | { leadId: string; status: 'ACCEPTED' }
  | { leadId: string; status: 'REJECTED'; rejectionReason: LeadRejectionReason };

export function mapDispatchToLeadRow(dispatch: LeadDispatchDto): LeadTableRow {
  return {
    id: dispatch.id,
    address: {
      locality: dispatch.project.address.locality,
      region: dispatch.project.address.region,
      postalCode: dispatch.project.address.postalCode,
    },
    metrics: dispatch.project.metrics,
    pricing: dispatch.project.pricing,
    storage: dispatch.project.storage ?? null,
    expiresAt: dispatch.expiresAt,
    projectId: dispatch.project.id,
    meta: {
      createdAt: dispatch.meta.createdAt,
      updatedAt: dispatch.status?.statusUpdatedAt ?? dispatch.meta.updatedAt ?? dispatch.meta.createdAt,
    },
  };
}

function sortLeads(leads: LeadTableRow[], { sortField, sortDirection = 'desc' }: GetLeadsParams): LeadTableRow[] {
  if (!sortField) return leads;

  return [...leads].toSorted((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'location':
        comparison = `${a.address.locality}, ${a.address.region}`.localeCompare(
          `${b.address.locality}, ${b.address.region}`,
        );
        break;
      case 'systemSize':
        comparison = a.metrics.systemSize - b.metrics.systemSize;
        break;
      case 'battery':
        comparison = (a.storage?.capacity ?? -1) - (b.storage?.capacity ?? -1);
        break;
      case 'pricePerWatt':
        comparison = a.pricing.ppw - b.pricing.ppw;
        break;
      case 'statusDate':
        comparison = (a.expiresAt ?? a.meta.updatedAt).localeCompare(b.expiresAt ?? b.meta.updatedAt);
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
}

export const LEADS_PAGE_SIZE = 5;
export const LEADS_EXPORT_MAX = 10_000;

function filterLeads(leads: LeadTableRow[], params: GetLeadsParams): LeadTableRow[] {
  const {
    search,
    states,
    minBatteryKwh,
    maxBatteryKwh,
    minSystemSize,
    maxSystemSize,
    minPricePerWatt,
    maxPricePerWatt,
    dateFrom,
    dateTo,
  } = params;

  let result = leads;

  if (search) {
    const term = search.toLowerCase();
    result = result.filter(
      (l) =>
        `${l.address.locality}, ${l.address.region} ${l.address.postalCode}`.toLowerCase().includes(term) ||
        `${l.metrics.systemSize}`.includes(term) ||
        `${l.pricing.ppw}`.includes(term),
    );
  }

  if (states && states.length > 0) {
    const stateSet = new Set(states);
    result = result.filter((l) => stateSet.has(l.address.region));
  }

  if (minBatteryKwh !== undefined) {
    result = result.filter((l) => l.storage !== null && (l.storage?.capacity ?? 0) >= minBatteryKwh);
  }

  if (maxBatteryKwh !== undefined) {
    result = result.filter((l) => l.storage !== null && (l.storage?.capacity ?? 0) <= maxBatteryKwh);
  }

  if (minSystemSize !== undefined) {
    result = result.filter((l) => l.metrics.systemSize >= minSystemSize);
  }

  if (maxSystemSize !== undefined) {
    result = result.filter((l) => l.metrics.systemSize <= maxSystemSize);
  }

  if (minPricePerWatt !== undefined) {
    result = result.filter((l) => l.pricing.ppw >= minPricePerWatt);
  }

  if (maxPricePerWatt !== undefined) {
    result = result.filter((l) => l.pricing.ppw <= maxPricePerWatt);
  }

  if (dateFrom) {
    result = result.filter((l) => l.meta.updatedAt.slice(0, 10) >= dateFrom);
  }

  if (dateTo) {
    result = result.filter((l) => l.meta.updatedAt.slice(0, 10) <= dateTo);
  }

  return result;
}

function paginateLeads(allLeads: LeadTableRow[], params: GetLeadsParams): GetLeadsResponse {
  const filtered = filterLeads(allLeads, params);
  const sorted = sortLeads(filtered, params);
  const page = params.pageNum ?? 1;
  const limit = params.pageSize ?? LEADS_PAGE_SIZE;
  const start = (page - 1) * limit;
  return { leads: sorted.slice(start, start + limit), total: filtered.length };
}

const leadsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getRejectedLeads: builder.query<GetLeadsResponse, GetLeadsParams>({
      queryFn: async (params, _api, _extra, baseQuery) => {
        const result = await baseQuery('/leads?pageSize=20');
        if (result.error) return { error: result.error };
        const { data } = result.data as GetLeadsApiResponse;
        const leads = data.filter((d) => d.status.code === 'REJECTED').map(mapDispatchToLeadRow);
        return { data: paginateLeads(leads, params) };
      },
      providesTags: ['Leads'],
    }),
    getExpiredLeads: builder.query<GetLeadsResponse, GetLeadsParams>({
      queryFn: async (params, _api, _extra, baseQuery) => {
        const result = await baseQuery('/leads?pageSize=20');
        if (result.error) return { error: result.error };
        const { data } = result.data as GetLeadsApiResponse;
        const leads = data
          .filter((d) => d.status.code === 'WITHDRAWN' && d.status.reason === 'EXPIRED')
          .map(mapDispatchToLeadRow);
        return { data: paginateLeads(leads, params) };
      },
      providesTags: ['Leads'],
    }),
    getPendingLeads: builder.query<GetLeadsResponse, GetLeadsParams>({
      queryFn: async (params, _api, _extra, baseQuery) => {
        const result = await baseQuery('/leads?pageSize=20');
        if (result.error) return { error: result.error };
        const { data } = result.data as GetLeadsApiResponse;
        const leads = data.filter((d) => d.status.code === 'PENDING').map(mapDispatchToLeadRow);
        return { data: paginateLeads(leads, params) };
      },
      providesTags: ['Leads'],
    }),
    getAcceptedLeads: builder.query<GetLeadsResponse, GetLeadsParams>({
      queryFn: async (params, _api, _extra, baseQuery) => {
        const result = await baseQuery('/leads?pageSize=20');
        if (result.error) return { error: result.error };
        const { data } = result.data as GetLeadsApiResponse;
        const leads = data.filter((d) => d.status.code === 'ACCEPTED').map(mapDispatchToLeadRow);
        return { data: paginateLeads(leads, params) };
      },
      providesTags: ['Leads'],
    }),
    updateLeadStatus: builder.mutation<void, UpdateLeadStatusArgs>({
      query: (args) => ({
        url: `leads/${args.leadId}/status`,
        method: 'PATCH',
        body:
          args.status === 'REJECTED'
            ? { status: args.status, rejectionReason: args.rejectionReason }
            : { status: args.status },
      }),
      invalidatesTags: ['Leads', 'History'],
    }),
  }),
});

export const {
  useGetRejectedLeadsQuery,
  useGetExpiredLeadsQuery,
  useGetPendingLeadsQuery,
  useGetAcceptedLeadsQuery,
  useLazyGetAcceptedLeadsQuery,
  useUpdateLeadStatusMutation,
} = leadsApi;
